// @flow
import React, { useState, useEffect, useCallback } from 'react';
import { withRouter } from 'react-router';
import { useSelector } from 'react-redux';
import { errorLogger } from '../../helpers';
import { CustomerTypes, ToggleKeys, VendorTypes } from '../../Constants';
import AddSubscription from 'views/Add';
import AddSubscriptionComviq from 'views/AddSubscriptionComviq';
import NotEntitled from 'views/NotEntitled';
import Login from 'views/Login';
import api from 'api';
import SmsChallenge from 'views/SmsChallenge';
import Loading from 'components';
import Maintenance from 'views/Maintenance';
import Error from 'views/Error';
import { isAppleDevice, isBusinessCustomer, isComviqCustomer, isSamsungDevice } from '../../helpers/customerTypeHelpers';
import { getContentFor } from '../../helpers/contentHelpers';

type Props = {
  match: {
    params: {
      imsi: string,
      eid: string,
    },
  },
};

const Home = ({
  match: {
    params: { imsi: testImsi, eid: testEid },
  },
}: Props) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showWall, setShowWall] = useState(true);
  const [userData, setUserData] = useState({});
  const [entitledStatus, setEntitledStatus] = useState({
    isEntitled: true,
    isAllowedPriceplan: true,
    isAllowedMoreSubscriptions: true,
    errorMessage: '',
    isInvalidCustomerType: false,
    isPrepaid: false,
  });

  const customerType = useSelector((state) => state.app.customerType);
  const isComviq = isComviqCustomer(customerType);
  const toggles = useSelector((state) => state.app.toggles);

  const content = getContentFor(customerType);

  function setWallToggle(toggleKey) {
    let wall = true;

    if (toggleKey) {
      wall = isToggledOn(ToggleKeys.ALL[window.__VENDOR__]) || isToggledOn(toggleKey);
    }
    setShowWall(wall);
  }

  function isToggledOn(toggleName) {
    if (!toggles) {
      errorLogger({ name: 'client-wall-toggle', log: 'toggles are missing' });
      return true;
    }

    const toggle = toggles
      .filter((toggle) => toggle && toggle.toggleId)
      .find((toggle) => toggle.toggleId.includes(toggleName));
    return toggle ? toggle.isToggledOn : true;
  }

  const handleMaintenanceSuccessfullLogin = useCallback(() => {
    setShowWall(false);
  }, []);

  const setLoggedInStatus = useCallback(
    (value) => {
      setIsLoggedIn(value);
    },
    [isLoggedIn]
  );

  const setUserAuthData = useCallback(
    (data) => {
      setUserData(data);
      if (
        (!data.isAllowedMoreSubscriptions && !data.hasUncompletedOrder) ||
        !data.isAllowedPriceplan ||
        data.errorMessage ||
        data.isPrepaid
      ) {
        setEntitledStatus({
          ...entitledStatus,
          isEntitled: false,
          isAllowedPriceplan: userData.isAllowedPriceplan,
          isAllowedMoreSubscriptions: userData.isAllowedMoreSubscriptions,
          errorMessage: data.errorMessage,
          isInvalidCustomerType: false,
          isPrepaid: data.isPrepaid,
        });
      }
      setIsLoading(false);
    },
    [userData, customerType]
  );

  const setUserEntitled = useCallback(() => {
    const isInvalidCustomerType = !!customerType && customerType === CustomerTypes.INVALID_CUSTOMER_TYPE;

    if (isInvalidCustomerType) {
      setEntitledStatus({
        ...entitledStatus,
        isEntitled: false,
        isAllowedPriceplan: false,
        isAllowedMoreSubscriptions: false,
        isInvalidCustomerType: isInvalidCustomerType,
      });
    }
  }, [userData, customerType]);

  const fetchData = async () => {
    try {
      // Get subscription information
      const postData = window.__POST_DATA__;
      let jsonWebToken;

      if (!!postData && postData !== 'undefined' && postData !== 'null') {
        const newData = window.atob(postData);
        jsonWebToken = JSON.parse(newData).carrierPostData;
      }
      const data = {
        postData: window.__POST_DATA__,
        msisdn: testImsi,
        eid: testEid,
        token: jsonWebToken,
      };
      const result = await api.getSubscriptions(data);
      if (result.error) {
        errorLogger({
          name: 'client-home-get-subscription',
          log: 'Get subscriptions request responded with error.',
          data: result.error,
        });
      } else {
        setUserAuthData(result);
      }
    } catch (error) {
      errorLogger({
        name: 'client-home-get-subscription',
        log: 'Get subscription information failed. With error message: ' + error.message,
        data: error.message,
      });
    }
  };
  useEffect(() => {
    if (customerType && toggles) {
      const wallIsToggledOn = ToggleKeys.VENDOR[window.__VENDOR__] + customerType + '-wall';

      setWallToggle(wallIsToggledOn);
      setUserEntitled();

      if (wallIsToggledOn) {
        setIsLoading(false);
      }
    }
  }, [customerType, toggles]);

  const {
    isAllowedPriceplan,
    isAllowedMoreSubscriptions,
    errorMessage,
    subscriptionProductName,
    ssn,
    msisdn,
    secondaryEid,
    primaryImsi,
    name,
    companionPriceplans,
    hasUncompletedOrder,
    orderId,
    hasAvailablePromotion,
    hasIncludedSubscription,
  } = userData;

  const { isEntitled, isInvalidCustomerType, isPrepaid } = entitledStatus;
  // useEffect(() => {
  //   const wallIsToggledOn = customerType && setWallToggle(ToggleKeys.VENDOR[window.__VENDOR__] + customerType + '-wall');

  //   if (wallIsToggledOn && isAppleDevice()) {
  //     setIsLoading(false);
  //   }
  // }, [customerType]);

  useEffect(() => {
    if (!showWall) {
      if (isSamsungDevice() && customerType) {
        setIsLoading(true);
        setIsLoggedIn(true);
        fetchData();
      }
    }
  }, [showWall]);

  if (!content || isLoading) return <Loading />;

  if (showWall && !isLoggedIn) {
    return <Maintenance onValidLogin={handleMaintenanceSuccessfullLogin} message={content.page.maintenance.htmlText} />;
  }

  if (customerType === CustomerTypes.INVALID_CUSTOMER_TYPE && typeof customerTypeContentKey === 'undefined') {
    return <Error message={content.page.entitled.numberNotEligibleError.htmlText} />;
  }

  return (
    <>
      {!isEntitled ? (
        <NotEntitled
          isAllowedMoreSubscriptions={isAllowedMoreSubscriptions}
          isAllowedPriceplan={isAllowedPriceplan}
          errorMessage={errorMessage}
          isInvalidCustomerType={isInvalidCustomerType}
          isPrepaid={isPrepaid}
        />
      ) : !isLoggedIn && isAppleDevice() ? (
        customerType === CustomerTypes.FUTUR || isComviq ? (
          <Login
            setLoggedInStatus={setLoggedInStatus}
            setUserAuthData={setUserAuthData}
            msisdn={testImsi}
            eid={testEid}
          />
        ) : isBusinessCustomer(customerType) ? (
          <SmsChallenge setLoggedInStatus={setLoggedInStatus} setUserAuthData={setUserAuthData} eid={testEid} />
        ) : (
          <Error message={content.page.entitled.numberNotEligibleError.htmlText} />
        )
      ) : isComviq ? (
        <AddSubscriptionComviq
          msisdn={msisdn}
          eid={secondaryEid}
          ssn={ssn}
          pricePlanName={subscriptionProductName}
          primaryImsi={primaryImsi}
          name={name}
          companionPriceplans={companionPriceplans}
          hasUncompletedOrder={hasUncompletedOrder}
          orderId={orderId}
          displayPromotion={hasAvailablePromotion}
        />
      ) : (
        <AddSubscription
          msisdn={msisdn}
          eid={secondaryEid}
          ssn={ssn}
          pricePlanName={subscriptionProductName}
          primaryImsi={primaryImsi}
          name={name}
          companionPriceplans={companionPriceplans}
          hasUncompletedOrder={hasUncompletedOrder}
          orderId={orderId}
          displayPromotion={hasAvailablePromotion}
          hasIncludedSubscription={hasIncludedSubscription}
        />
      )}
    </>
  );
};

export default withRouter(Home);
